import { AccuracyInspectionIconEnum, NetworkAssessmentAnalysisType, ProviderNetworkType, TimeFrequency } from "@app/projects/enumerators";
import { AccuracyDetailSpecialtyGroupsByRegion } from "@app/projects/network-assessment/accuracy";
import {
  AccuracyInspectionResponse,
  AccuracyProjectResults,
  AccuracyProviderDetail,
  AccuracyProviderResponse,
  AccuracySpecialtyGroupAggregate,
  AccuracySummaryByRegionSet,
  AccuracyViewByAllResponse,
  AccuracyViewByRegionResponse
} from "@app/projects/results/accuracy";
import { AccuracyTrendChartItem, ResultRegion } from "@app/projects/results/common";
import { ProjectionProvidersByProjectionType } from "@app/projects/results/projection-provider";
import { ServicingProviderComparisonResult, ServicingProviderResult } from "@app/projects/results/servicing-provider";
import { ServiceBase } from "@app/shared";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { SearchAddress } from "@app/tools/accuracy-evidence/models/searchAddress";

/** Handles communication across the wire when retrieving and interacting with projects. */
export default class AccuracyService extends ServiceBase {

  /**
   * Retrieves project detail results for a specific project configuration ID,
   * including actual results for the service area of the project, the specialty groups
   * that are part of the project, and the state regions that results can be found for.
   * The state regions should always have result entries.
   * @param projectId The project identifier
   * @param projectRunConfigurationId The project run configuration identifier
   * @param networkAssessmentAnalysisType The analysis type identifier
   */
  public async detailResults(projectId: number, projectRunConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AccuracyProjectResults> {
    if (!projectId || !projectRunConfigurationId) {
      return null;
    }

    const res = await this.makeOptionalGet<AccuracyProjectResults>(`accuracy/projects/${projectId}/run-configurations/${projectRunConfigurationId}/detail-results?networkAssessmentAnalysisType=${networkAssessmentAnalysisType}`);

    return res || null;
  }

  /**
   * Retrieves the results for a given project, run configuration, and set of regions by batching the calls by number of region set ids.
   * @param projectId The identifier of the project.
   * @param regionSetIds The region sets associated with the desired results.
   * @param runConfigurationId The run configuration associated with the desired results.
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   */
  public async regionResultsBulkBatched(customProjectId: number, regionSetIds: number[], projectRunConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AccuracyViewByRegionResponse[]> {
    if (!customProjectId || !regionSetIds || !projectRunConfigurationId) {
      return null;
    }

    const batchSize = 20;
    let results: AccuracyViewByRegionResponse[] = [];
    for (let batch = 0; batch < regionSetIds.length / batchSize; batch++) {
      const batchRegionSetIds = regionSetIds.slice(batch * batchSize, (batch + 1) * batchSize);
      const batchResult = await this.makePost<AccuracyViewByRegionResponse>(`accuracy/projects/${customProjectId}/run-configurations/${projectRunConfigurationId}/summary-results?networkAssessmentAnalysisType=${networkAssessmentAnalysisType}`,
        { regionSetIds: batchRegionSetIds });

      results = results.concat(batchResult);
    }

    return results;
  }

  /**
   * Retrieves the results for a given project, run configuration, and set of regions.
   * @param projectId The identifier of the project.
   * @param regionSetIds The region sets associated with the desired results.
   * @param runConfigurationId The run configuration associated with the desired results.
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   */
  public async regionResultsBulk(customProjectId: number, regionSetIds: number[], projectRunConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AccuracyViewByRegionResponse[]> {
    if (!customProjectId || !regionSetIds || !projectRunConfigurationId) {
      return null;
    }

    const result = await this.makePost<AccuracyViewByRegionResponse>(`accuracy/projects/${customProjectId}/run-configurations/${projectRunConfigurationId}/summary-results?networkAssessmentAnalysisType=${networkAssessmentAnalysisType}`,
        { regionSetIds: regionSetIds });

    return [result];
  }

  /**
   * Retrieves the results for a given project, region, and run configuration.
   * @param projectId The project identifier
   * @param regionSetId The region set identifier
   * @param projectRunConfigurationId The project run configuration identifier
   * @param networkAssessmentAnalysisType The analysis type identifier
   */
  public async specialtyGroupResults(projectId: number, regionSetId: number, projectRunConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AccuracySpecialtyGroupAggregate[]> {
    // If we don't have a projectId, regionSetId or projectRunConfigurationId, return early.
    if (!projectId || !regionSetId || !projectRunConfigurationId) {
      return null;
    }

    return await this.makeGet<AccuracySpecialtyGroupAggregate[]>(`accuracy/projects/${projectId}/run-configurations/${projectRunConfigurationId}/region-sets/${regionSetId}/detail-results?networkAssessmentAnalysisType=${networkAssessmentAnalysisType}`);
  }

  /**
   * Retrieves the results for a given project, run configuration, and set of regions.
   * @param projectId The identifier of the project.
   * @param regionSetIds The region sets associated with the desired results.
   * @param runConfigurationId The run configuration associated with the desired results.
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   */
    public async specialtyGroupResultsBulk(projectId: number, specialtyGroupCode: string, regionSets: ResultRegion[], runConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AccuracyDetailSpecialtyGroupsByRegion[]> {
      // If we don't have a projectId, regionSetId or runConfigurationId, return early.
      if (!projectId || !regionSets || !runConfigurationId || !networkAssessmentAnalysisType) {
        return null;
      }

      const ids = regionSets.map((rs) => rs.regionSetId);

      return await this.makePost<AccuracyDetailSpecialtyGroupsByRegion[]>(
        `accuracy/projects/${projectId}/runconfigurations/${runConfigurationId}/regionsets/detail-results/bulk`, { networkAssessmentAnalysisType, regionSetIds: ids, specialtyGroupCode });
    }

  /**
   * Retrieves the results for a given project, region, and run configuration.
   * @param projectId The project identifier
   * @param regionSetId The region set identifier
   * @param projectRunConfigurationId The project run configuration identifier
   * @param networkAssessmentAnalysisType The analysis type identifier
   */
  public async regionDetails(customProjectId: number, regionSetId: number, projectRunConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AccuracySummaryByRegionSet[]> {
    // Return early if we don't have any of the good stuff
    if (!customProjectId || !regionSetId || !projectRunConfigurationId) {
      return null;
    }

    return await this.makeGet<AccuracySummaryByRegionSet[]>(`accuracy/projects/${customProjectId}/run-configurations/${projectRunConfigurationId}/region-sets/${regionSetId}/summaries?networkAssessmentAnalysisType=${networkAssessmentAnalysisType}`);
  }

  public async viewByAll(customProjectId: number, regionSetIds: number[], projectRunConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, skip?: number, take?: number, sortDirection?: SortDirection, selectedColumn?: string, sortColor?: string): Promise<AccuracyViewByAllResponse> {
    // Return early if we don't have any of the good stuff
    if (!customProjectId || !regionSetIds.length || !projectRunConfigurationId || !networkAssessmentAnalysisType) {
      return null;
    }

    return await this.makePost<AccuracyViewByAllResponse>(`accuracy/projects/${customProjectId}/run-configurations/${projectRunConfigurationId}/summaries/view-by-all?networkAssessmentAnalysisType=${networkAssessmentAnalysisType}`, {
      regionSetIds,
      selectedColumn,
      skip,
      sortColor,
      sortDirection,
      take,
    });
  }

  /**
   * Retrieves the accuracy detail results for a given project, region, run-configuration & specialty-group-code
   */
  public async regionDetailsByRegionSet(projectId: number, regionSetId: number, runConfigurationId: number, specialtyGroupCode: string, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AccuracySummaryByRegionSet[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode) {
      return null;
    }

    return await this.makeGet<AccuracySummaryByRegionSet[]>(`accuracy/projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/summaries/by-region-set`, { networkAssessmentAnalysisType, specialtyGroupCode });
  }

  /**
   * Retrieves the servicing providers for a given project, region, run-configuration, specialty-group-code & provider-network-type
   */
  public async servicingProviders(projectId: number, regionSetId: number, runConfigurationId: number, specialtyGroupCode: string, providerNetworkType: ProviderNetworkType, skip?: number, take?: number): Promise<ServicingProviderResult> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !providerNetworkType) {
      return null;
    }
    return await this.makeGet<ServicingProviderResult>(`accuracy/projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/providers/${providerNetworkType}`, { skip, specialtyGroupCode, take });
  }

  /**
   * Retrieves servicing providers for a specific project run configuration, region set, specialty group, and provider network type,
   * compared against the set of servicing providers for a different project run configuration.
   * This sucker returns early with null, if it smells something is up with the inputs. 💩
   * @param projectId The project identifier
   * @param selectedProjectRunConfigurationId The selected project run configuration ID
   * @param comparedProjectRunConfigurationId The project run configuration ID of the configuration that is being compared to the selected
   * @param regionSetId The region set identifier
   * @param specialtyGroupCode The specialty group code
   * @param providerNetworkType The provider network type
   * @param skip The skip component of retrieving results in batch
   * @param take The take component of retrieving results in batch
   * @param added Set to return providers that fall into the added group
   * @param removed Set to return providers that fall into the removed group
   * @param matched Set to return providers that fall in to the matched group
   */
  public async getServicingComparisonProviders(projectId: number, selectedProjectRunConfigurationId: number, comparedProjectRunConfigurationId: number, regionSetId: number, specialtyGroupCode: string, providerNetworkType: ProviderNetworkType, added?: boolean, removed?: boolean, matched?: boolean, skip?: number, take?: number): Promise<ServicingProviderComparisonResult> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !selectedProjectRunConfigurationId || !comparedProjectRunConfigurationId || !specialtyGroupCode || !providerNetworkType) {
      return null;
    }

    return await this.makePost<ServicingProviderComparisonResult>(`accuracy/projects/${projectId}/comparison-providers`, { added, comparedProjectRunConfigurationId, flatten: true, matched, providerNetworkType, regionSetId, removed, selectedProjectRunConfigurationId, skip, specialtyGroupCode, take });
  }

  /**
   * Retrieves the impact-projection-provider detail for a given project, region, run-configuration, specialty-group-code & provider-network-type
   * @param projectId
   * @param regionSetId
   * @param runConfigurationId
   * @param specialtyGroupCode
   * @param providerNetworkType
   */
  public async getImpactProjectionProviders(projectId: number, regionSetId: number, runConfigurationId: number, specialtyGroupCode: string, providerNetworkType: ProviderNetworkType, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<ProjectionProvidersByProjectionType[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !providerNetworkType || !networkAssessmentAnalysisType) {
      return null;
    }
    return await this.makeGet<ProjectionProvidersByProjectionType[]>(`accuracy/projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/projection-providers/${providerNetworkType}`, { networkAssessmentAnalysisType, specialtyGroupCode });
  }

  /**
   * Retreives Accuracy results
   */
  public async accuracyAggregateInspectionResults(projectId: number, runConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, regionSetId: number, specialtyGroupCode: string): Promise<AccuracyInspectionResponse> {
    return await this.makeGet<AccuracyInspectionResponse>(`accuracy/projects/${projectId}/run-configurations/${runConfigurationId}/inspection`, {
      networkAssessmentAnalysisType,
      regionSetId,
      specialtyGroupCode,
    });
  }

  public async accuracyProviders(
    projectId: number, runConfigurationId: number, providerNetworkType: ProviderNetworkType, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, regionSetId: number,
    specialtyGroupCode?: string, inspectionUid?: string, skip?: number, take?: number, selectedColumn?: string, sortDirection?: SortDirection, sortIcon?: AccuracyInspectionIconEnum, sortColor?: string, searchString?: string, bandFilter?: string, noCounty?: boolean): Promise<AccuracyProviderResponse>
  {
    return await this.makeGet<AccuracyProviderResponse>(`accuracy/projects/${projectId}/run-configurations/${runConfigurationId}/providers/${providerNetworkType}`, {
      bandFilter,
      inspectionUid,
      networkAssessmentAnalysisType,
      noCounty,
      regionSetId,
      searchString,
      selectedColumn,
      skip,
      sortColor,
      sortDirection,
      sortIcon,
      specialtyGroupCode,
      take,
    });
  }

  public async accuracyProviderDetail(projectId: number, runConfigurationId: number, regionSetSpecialtyGroupId: number, providerNetworkType: ProviderNetworkType, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, providerId: number): Promise<AccuracyProviderDetail> {
    return await this.makeGet<AccuracyProviderDetail>(`accuracy/projects/${projectId}/run-configurations/${runConfigurationId}/region-set-specialty-groups/${regionSetSpecialtyGroupId}/providers/${providerNetworkType}/${networkAssessmentAnalysisType}/provider/${providerId}`);
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  public async getEvidenceByNpiTabular(npi: string, asOfDate?: Date): Promise<any> {
    return await asOfDate ? this.makeGet<any>(`verifieddata/tabular/npi/${npi}?asOfDate=${asOfDate.toISOString()}`) :
      this.makeGet<any>(`verifieddata/tabular/npi/${npi}`);
  }

  public async getEvidenceByXidTabular(xid: string, asOfDate?: Date): Promise<any> {
    return await asOfDate ? this.makeGet<any>(`verifieddata/tabular/xid/${xid}?asOfDate=${asOfDate.toISOString()}`) :
      this.makeGet<any>(`verifieddata/tabular/xid/${xid}`);
  }

  public async getEvidenceByAddressTabular(address: SearchAddress, asOfDate?: Date): Promise<any> {
    const addressQuery = `?address1=${address.address1}&address2=${address.address2}&city=${address.city}&state=${address.state}&zip=${address.zip}`;
    return await asOfDate ? this.makeGet<any>(`verifieddata/tabular/address${addressQuery}&asOfDate=${asOfDate.toISOString()}`) :
      this.makeGet<any>(`verifieddata/tabular/address${addressQuery}`);
  }
  /* eslint-enable */

  /* ****************
   * Trend Chart Data
   * **************** */

  public async getTrendChartData(
    projectId: number,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    runFrequency: TimeFrequency,
    regionSetId?: number,
    specialtyGroupCode?: string,
    inspection?: string): Promise<AccuracyTrendChartItem[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !runFrequency) {
      return null;
    }

    return await this.makeGet<AccuracyTrendChartItem[]>(`accuracy/projects/${projectId}/trendgraphdata/${runFrequency}`, { inspection, networkAssessmentAnalysisType, regionSetId, specialtyGroupCode });
  }
}
